import { createFileRoute } from '@tanstack/react-router'
import { StaticNav } from '@/components/static-nav'
import { StaticFooter } from '@/components/static-footer'
import { SignInButton } from '@/components/sign-in-button'
import {
  IconTrash,
  IconSparkles,
  IconArchive,
  IconMailOff,
  IconFilter,
  IconClock,
  IconBrain,
  IconCheck,
  IconBolt
} from '@tabler/icons-react'

export const Route = createFileRoute('/email-cleanup')({
  component: EmailCleanupPage,
})

function EmailCleanupPage() {
  const stats = [
    { value: "28%", label: "of workday spent on email" },
    { value: "2+ hrs", label: "daily email management" },
    { value: "121", label: "average emails received daily" },
  ]

  const steps = [
    {
      icon: <IconTrash className="w-6 h-6" />,
      title: "Start with Bulk Delete",
      description: "Identify categories of emails you can safely delete in bulk",
      items: [
        "Promotional emails older than 30 days",
        "Social media notifications",
        "Automated receipts and confirmations",
        "Newsletter archives you'll never read"
      ]
    },
    {
      icon: <IconMailOff className="w-6 h-6" />,
      title: "Unsubscribe from Unwanted Senders",
      description: "For every promo email you delete, there's another one coming",
      items: [
        "Take time to unsubscribe, not just delete",
        "Focus on high-volume senders first",
        "Use bulk unsubscribe tools to save time"
      ]
    },
    {
      icon: <IconArchive className="w-6 h-6" />,
      title: "Archive Important Emails",
      description: "Not everything needs to be deleted",
      items: [
        "Archive emails you might need to reference",
        "They'll be searchable but won't clutter inbox",
        "Use labels for easy organization"
      ]
    },
    {
      icon: <IconFilter className="w-6 h-6" />,
      title: "Set Up Filters for the Future",
      description: "Prevent future inbox clutter automatically",
      items: [
        "Auto-sort incoming mail by sender",
        "Auto-archive low-priority emails",
        "Skip inbox for certain categories"
      ]
    }
  ]

  const maintenanceTips = [
    { icon: <IconClock className="w-5 h-5" />, tip: "Process emails daily using 'touch it once' principle" },
    { icon: <IconMailOff className="w-5 h-5" />, tip: "Unsubscribe immediately from unwanted emails" },
    { icon: <IconFilter className="w-5 h-5" />, tip: "Use labels and folders to organize important emails" },
    { icon: <IconBolt className="w-5 h-5" />, tip: "Schedule regular inbox cleanup sessions" },
  ]

  return (
    <div className="bg-[#fafafa] text-slate-950 min-h-screen">
      <StaticNav />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-32 pb-24">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-green-100/40 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-green-50 border border-green-100">
              <IconTrash className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">
                Comprehensive guide
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-slate-900 mb-6">
              Email Cleanup:{' '}
              <span className="text-green-600">Clean Your Gmail Inbox</span>
            </h1>

            <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              A cluttered inbox is more than an eyesore—it's a productivity killer.
              Learn strategies to clean up your email and maintain a pristine inbox.
            </p>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 max-w-lg mx-auto">
              {stats.map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold text-slate-900">{stat.value}</div>
                  <div className="text-xs text-slate-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Step-by-Step Guide */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Step-by-Step Email Cleanup Guide
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Follow these four steps to transform your chaotic inbox into an organized system
          </p>

          <div className="space-y-6">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-6 p-6 rounded-3xl bg-white shadow-sm ring-1 ring-slate-200">
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-3xl bg-green-100 text-green-600 flex items-center justify-center">
                    {step.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-medium text-green-600 bg-green-100 px-2 py-0.5 rounded">
                      Step {i + 1}
                    </span>
                    <h3 className="text-xl font-semibold text-slate-900">{step.title}</h3>
                  </div>
                  <p className="text-slate-600 mb-4">{step.description}</p>
                  <ul className="grid md:grid-cols-2 gap-2">
                    {step.items.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <IconCheck className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-8 md:p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-white/20 text-sm font-medium">
                  <IconSparkles className="w-4 h-4" />
                  AI-Powered
                </div>
                <h2 className="text-3xl font-bold mb-4">
                  Using AI for Email Cleanup
                </h2>
                <p className="text-blue-100 mb-6">
                  Modern AI tools like Winbox Zero can analyze thousands of emails in minutes,
                  automatically categorizing them into Inbox, Delete, Archive, and Unsubscribe.
                </p>
                <ul className="space-y-3">
                  {[
                    "Analyze up to 5,000 emails",
                    "AI categorizes in minutes, not hours",
                    "One-click bulk actions",
                    "Visual Kanban-style organization"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <IconCheck className="w-5 h-5 text-green-400" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex justify-center">
                <div className="bg-white/10 backdrop-blur rounded-3xl p-6">
                  <IconBrain className="w-24 h-24 text-white/80 mx-auto" />
                  <div className="text-center mt-4">
                    <div className="text-2xl font-bold">5,000+</div>
                    <div className="text-blue-200 text-sm">emails analyzed in minutes</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Tips */}
      <section className="py-24 bg-white border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-slate-900 mb-4 text-center">
            Maintaining a Clean Inbox
          </h2>
          <p className="text-lg text-slate-600 text-center mb-12 max-w-2xl mx-auto">
            Keep your inbox clean with these daily habits
          </p>

          <div className="grid md:grid-cols-2 gap-4">
            {maintenanceTips.map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-3xl border border-slate-200 hover:border-green-200 hover:bg-green-50/50 transition-colors">
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-green-100 text-green-600 flex items-center justify-center">
                  {item.icon}
                </div>
                <p className="text-slate-700 pt-2">{item.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="border-t border-slate-200 bg-[#fafafa] px-6 py-32">
        <div className="mx-auto max-w-4xl">
          <div className="relative overflow-hidden rounded-[2.5rem] bg-slate-950 px-8 py-20 shadow-2xl sm:px-16 sm:py-24 text-center">
            <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl mix-blend-screen" />
            <div className="absolute -bottom-24 -right-24 h-96 w-96 rounded-full bg-purple-500/20 blur-3xl mix-blend-screen" />
            
            <div className="relative z-10">
              <h2 className="mx-auto max-w-2xl text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                Clean your inbox with AI
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-slate-300">
                Get started today with unlimited email processing.
              </p>
              <div className="mt-10 flex flex-col items-center gap-4">
                <SignInButton variant="dark" />
              </div>
            </div>
          </div>
        </div>
      </section>

      <StaticFooter />
    </div>
  )
}
